import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Decimal from 'decimal.js';
import { useContext, useEffect, useState } from 'react';
import '../../App.css';
import FaultTable from '../../components/FaultTable';
import SnackbarContext from '../../components/SnackbarContext';
import RestfulManager, { RES_STATUS_OK } from '../../manager/RestfulManager';
import { StyleConstants, Styles } from '../../Styles';
import { getSqlDateByDatePicker, getMonthShortName, getSqlDate, getStartOfMonth, getEndOfMonth } from '../../utils/DateUtils';
import { jsonToQueryString } from '../../utils/StringUtils';
import dayjs from 'dayjs';
import SessionManager from '../../manager/SessionManager';
import fileDownload from 'js-file-download';

const datePickerStyle = {
    "& .MuiInputBase-input":{
        color: StyleConstants.fontColor,
    },
    "& .MuiSvgIcon-root":{
        fill: StyleConstants.fontColor,
    },
};

const barChartStyle = {
    "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel":{
        fill: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel":{
        fill: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-left .MuiChartsAxis-line":{
        stroke: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-bottom .MuiChartsAxis-line":{
        stroke: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-left .MuiChartsAxis-tick":{
        stroke: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-bottom .MuiChartsAxis-tick":{
        stroke: StyleConstants.fontColor,
    },
    "& .MuiChartsGrid-line":{
        stroke: StyleConstants.fontColor,
        strokeWidth: "0.5",
    },
    "& .MuiChartsAxis-label tspan":{
        fill: StyleConstants.fontColor,
    },
    "& .MuiChartsLegend-series tspan":{
        fill: StyleConstants.fontColor,
    },
    "& .MuiChartsAxis-directionY .MuiChartsAxis-label": {
        transform: "translateX(-10px) !important",
    },
};

const pieChartStyle = {
    [`& .${pieArcLabelClasses.root}`]: {
        fill: StyleConstants.fontColor,
    },
    "& .MuiChartsLegend-series tspan":{
        fill: StyleConstants.fontColor,
    },
};

const checkBoxStyle = {
    color: StyleConstants.fontColor,
    '&.Mui-checked': {
        color: StyleConstants.fontColor,
    },
};

const ReportPage = () => {
    const { setOpenSnackBar, setSnackbarSeverity, setSnackbarMessage } = useContext(SnackbarContext);

    const sessionManager = new SessionManager();
    const restfulManager = new RestfulManager();

    const [isLoading, setIsLoading] = useState(false);

    const [startDate, setStartDate] = useState(dayjs(getSqlDate(getStartOfMonth(new Date()))));
    const [endDate, setEndDate] = useState(dayjs(getSqlDate(getEndOfMonth(new Date()))));

    const [cars, setCars] = useState([]);
    const [carCode, setCarCode] = useState('');

    const [faultCatCodeAll, setFaultCatCodeAll] = useState(true);
    const [faultCatCodeVac, setFaultCatCodeVac] = useState(true);
    const [faultCatCodeSiv, setFaultCatCodeSiv] = useState(true);
    const [faultCatCodeVvvf, setFaultCatCodeVvvf] = useState(true);
    const [faultCatCodeDoor, setFaultCatCodeDoor] = useState(true);
    const [faultCatCodeBrake, setFaultCatCodeBrake] = useState(true);

    const [faults, setFaults] = useState([]);

    const [faultDistributionData, setFaultDistributionData] = useState([]);
    const [faultCountPerLrvData, setFaultCountPerLrvData] = useState([]);
    const [topFiveFaultData, setTopFiveFaultData] = useState([]);
    const [faultTrendData, setFaultTrendData] = useState([]);
    const [faultTrendSeries, setFaultTrendSeries] = useState([]);

    const [maxFaultCountPerLrv, setMaxFaultCountPerLrv] = useState(10);
    const [maxTopFiveFault, setMaxTopFiveFault] = useState(10);
    const [maxFaultTrend, setMaxFaultTrend] = useState(5);

    const carSelectChange = (event) => {
        const selectedCarCode = event.target.value;
        setCarCode(selectedCarCode);
    };

    const faultCatCodeAllClick = (event) => {
        setFaultCatCodeAll(event.target.checked);
        setFaultCatCodeVac(event.target.checked);
        setFaultCatCodeSiv(event.target.checked);
        setFaultCatCodeVvvf(event.target.checked);
        setFaultCatCodeDoor(event.target.checked);
        setFaultCatCodeBrake(event.target.checked);
    };

    const faultCatCodeVacClick = (event) => {
        if (event.target.checked === false) {
            setFaultCatCodeAll(false);
        } else {
            checkAllClick(event.target.checked, faultCatCodeSiv, faultCatCodeVvvf, faultCatCodeDoor, faultCatCodeBrake);
        }
        setFaultCatCodeVac(event.target.checked);
    };

    const faultCatCodeSivClick = (event) => {
        if (event.target.checked === false) {
            setFaultCatCodeAll(false);
        } else {
            checkAllClick(faultCatCodeVac, event.target.checked, faultCatCodeVvvf, faultCatCodeDoor, faultCatCodeBrake);
        }
        setFaultCatCodeSiv(event.target.checked);
    };

    const faultCatCodeVvvfClick = (event) => {
        if (event.target.checked === false) {
            setFaultCatCodeAll(false);
        } else {
            checkAllClick(faultCatCodeVac, faultCatCodeSiv, event.target.checked, faultCatCodeDoor, faultCatCodeBrake);
        }
        setFaultCatCodeVvvf(event.target.checked);
    };

    const faultCatCodeDoorClick = (event) => {
        if (event.target.checked === false) {
            setFaultCatCodeAll(false);
        } else {
            checkAllClick(faultCatCodeVac, faultCatCodeSiv, faultCatCodeVvvf, event.target.checked, faultCatCodeBrake);
        }
        setFaultCatCodeDoor(event.target.checked);
    };
    
    const faultCatCodeBrakeClick = (event) => {
        if (event.target.checked === false) {
            setFaultCatCodeAll(false);
        } else {
            checkAllClick(faultCatCodeVac, faultCatCodeSiv, faultCatCodeVvvf, faultCatCodeDoor, event.target.checked);
        }
        setFaultCatCodeBrake(event.target.checked);
    };

    const checkAllClick = (curFaultCatCodeVac, curFaultCatCodeSiv, curFaultCatCodeVvvf, curFaultCatCodeDoor, curFaultCatCodeBrake) => {
        if (
            (faultCatCodeAll === false)
            && (curFaultCatCodeVac === true)
            && (curFaultCatCodeSiv === true)
            && (curFaultCatCodeVvvf === true)
            && (curFaultCatCodeDoor === true)
            && (curFaultCatCodeBrake === true)
        ) {
            setFaultCatCodeAll(true);
        }
    };

    const searchReport = async () => {
        const faultCatCode = [];
        if (faultCatCodeVac === true) {
            faultCatCode.push('VAC');
        }
        if (faultCatCodeSiv === true) {
            faultCatCode.push('SIV');
        }
        if (faultCatCodeVvvf === true) {
            faultCatCode.push('VVVF');
        }
        if (faultCatCodeDoor === true) {
            faultCatCode.push('DOOR');
        }
        if (faultCatCodeBrake === true) {
            faultCatCode.push('BRAKE');
        }

        if (faultCatCode.length === 0) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Please choose at least one fault type.');
            setOpenSnackBar(true);
            return;
        }

        const payload = {
            startDate: startDate ? getSqlDateByDatePicker(startDate) : '',
            endDate: endDate ? getSqlDateByDatePicker(endDate) : '',
            carCode: carCode !== 'ALL' ? carCode : '',
            faultCatCode: faultCatCode ? faultCatCode : [''],
        };

        const query = jsonToQueryString(payload);

        setIsLoading(true);

        try {
            const res = await restfulManager.getFaults(sessionManager.getAccessToken(), query);
            const json = await res.json();
            if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                const curFaults = json.faultDTOs;
                setFaults(curFaults);

                let totalFaultCount = 0;

                const faultCatCodeCountMap = {};
                const carCodeCountMap = {};
                const faultTypeCodeCountMap = {};
                const monthCountMap = [];

                const curFultDistributionData = [];
                const curFaultCountPerLrvData = [];
                const curTopFiveFaultData = [];

                for (let i = 0; i < curFaults.length; i += 1) {
                    totalFaultCount += 1;

                    const curFault = curFaults[i];

                    // group by fault cat code
                    if (!faultCatCodeCountMap[curFault.faultCatCode]) {
                        faultCatCodeCountMap[curFault.faultCatCode] = 0;
                    }
                    faultCatCodeCountMap[curFault.faultCatCode] += 1;

                    // group by car code
                    if (!carCodeCountMap[curFault.carCode]) {
                        carCodeCountMap[curFault.carCode] = 0;
                    }
                    carCodeCountMap[curFault.carCode] += 1;

                    // group by fault type code
                    if (!faultTypeCodeCountMap[curFault.faultTypeCode]) {
                        faultTypeCodeCountMap[curFault.faultTypeCode] = 0;
                    }
                    faultTypeCodeCountMap[curFault.faultTypeCode] += 1;

                    // group by latest 7 year-month and car code
                    const displayDate = curFault.dateTime.split(' ')[0];
                    const displayDateSplit = displayDate.split('/');
                    const curYearMonth = displayDateSplit[2] + '-' + displayDateSplit[1];
                    let monthCountElement = monthCountMap.find((element) => element.yearMonth === curYearMonth);
                    if (!monthCountElement) {
                        monthCountElement = { yearMonth: curYearMonth }
                        monthCountMap.push(monthCountElement);
                    }
                    if (!monthCountElement[curFault.carCode]) {
                        monthCountElement[curFault.carCode] = 0;
                    }
                    monthCountElement[curFault.carCode] += 1;
                }

                // Fault Distribution Pie Chart
                const faultCatCodeCountKeys = Object.keys(faultCatCodeCountMap);
                for (let i = 0; i < faultCatCodeCountKeys.length; i += 1) {
                    const curFaultCatCode = faultCatCodeCountKeys[i];
                    const curCount = new Decimal(faultCatCodeCountMap[curFaultCatCode]);
                    const curPercent = curCount.dividedBy(totalFaultCount).times(100).toFixed(1);

                    curFultDistributionData.push({
                        label: curFaultCatCode + ' ' + String(curPercent) + '%',
                        value: faultCatCodeCountMap[curFaultCatCode],
                    });
                }
                setFaultDistributionData(curFultDistributionData);

                // Fault Count Per LRV
                let curMaxFaultCountPerLrv = maxFaultCountPerLrv;
                const carCodeCountMapKeys = Object.keys(carCodeCountMap);
                for (let i = 0; i < carCodeCountMapKeys.length; i += 1) {
                    const curCarCode = carCodeCountMapKeys[i];

                    curFaultCountPerLrvData.push({
                        carCode: curCarCode,
                        faultCount: carCodeCountMap[curCarCode],
                    });

                    if (carCodeCountMap[curCarCode] > curMaxFaultCountPerLrv) {
                        curMaxFaultCountPerLrv = carCodeCountMap[curCarCode];
                    }
                }
                setFaultCountPerLrvData(curFaultCountPerLrvData);
                if (curMaxFaultCountPerLrv > maxFaultCountPerLrv) {
                    setMaxFaultCountPerLrv(curMaxFaultCountPerLrv);
                }

                // Top 5 Faults
                let curMaxTopFiveFault = maxTopFiveFault;
                const faultTypeCodeCountKeys = Object.keys(faultTypeCodeCountMap);
                for (let i = 0; i < faultTypeCodeCountKeys.length; i += 1) {
                    const curFaultTypeCode = faultTypeCodeCountKeys[i];

                    curTopFiveFaultData.push({
                        faultCode: curFaultTypeCode,
                        faultCount: faultTypeCodeCountMap[curFaultTypeCode],
                    });

                    if (faultTypeCodeCountMap[curFaultTypeCode] > curMaxTopFiveFault) {
                        curMaxTopFiveFault = faultTypeCodeCountMap[curFaultTypeCode];
                    }
                }
                curTopFiveFaultData.sort((a, b) => { 
                    return b.faultCount - a.faultCount;
                });
                setTopFiveFaultData(curTopFiveFaultData.slice(0, 5));
                if (curMaxTopFiveFault > maxTopFiveFault) {
                    setMaxTopFiveFault(curMaxTopFiveFault);
                }

                // Fault Trend
                monthCountMap.sort((a, b) => {
                    return a.yearMonth.localeCompare(b.yearMonth);
                });
                const curFaultTrendData = monthCountMap.slice((monthCountMap.length - 7) < 0 ? 0 : (monthCountMap.length - 7), monthCountMap.length);

                let curMaxFaultTrend = maxFaultTrend;
                for (let i = 0; i < curFaultTrendData.length; i += 1) {
                    const curYearMonthSplit = curFaultTrendData[i]['yearMonth'].split('-');
                    curFaultTrendData[i]['yearMonth'] = curYearMonthSplit[0] + '-' + getMonthShortName(Number(curYearMonthSplit[1] - 1));

                    const curFaultTrendKeys = Object.keys(curFaultTrendData[i]);
                    for (let j = 0; j < curFaultTrendKeys.length; j += 1) {
                        if (curFaultTrendKeys[j] !== 'yearMonth') {
                            if (curFaultTrendData[i][curFaultTrendKeys[j]] > curMaxFaultTrend) {
                                curMaxFaultTrend = curFaultTrendData[i][curFaultTrendKeys[j]];
                            }
                        }
                    }
                }
                setFaultTrendData(curFaultTrendData);
                if (curMaxFaultTrend > maxFaultTrend) {
                    setMaxFaultTrend(curMaxFaultTrend);
                }

                const carCodeSet = new Set();
                for (let i = 0; i < curFaultTrendData.length; i += 1) {
                    const curKeys = Object.keys(curFaultTrendData[i]);
                    for (let j = 0; j < curKeys.length; j += 1) {
                        if (curKeys[j] !== 'yearMonth') {
                            carCodeSet.add(curKeys[j]);
                        }
                    }
                }

                const carCodeArray = new Array(...carCodeSet);
                carCodeArray.sort((a, b) => {
                    return a.localeCompare(b);
                });
                const curFaultTrendSeries = [];
                for (let i = 0; i < carCodeArray.length; i += 1) {
                    curFaultTrendSeries.push({
                        label: carCodeArray[i],
                        dataKey: carCodeArray[i],
                    });
                }
                setFaultTrendSeries(curFaultTrendSeries);
            }
        } catch (e) {
            console.error(e);
            setSnackbarSeverity('error');
            setSnackbarMessage('Error. Please contact administrator.');
            setOpenSnackBar(true);
        }

        setIsLoading(false);
    };

    const exportCsv = () => {
        const faultCatCode = [];
        if (faultCatCodeVac === true) {
            faultCatCode.push('VAC');
        }
        if (faultCatCodeSiv === true) {
            faultCatCode.push('SIV');
        }
        if (faultCatCodeVvvf === true) {
            faultCatCode.push('VVVF');
        }
        if (faultCatCodeDoor === true) {
            faultCatCode.push('DOOR');
        }
        if (faultCatCodeBrake === true) {
            faultCatCode.push('BRAKE');
        }

        if (faultCatCode.length === 0) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Please choose at least one fault type.');
            setOpenSnackBar(true);
            return;
        }

        const payload = {
            startDate: startDate ? getSqlDateByDatePicker(startDate) : '',
            endDate: endDate ? getSqlDateByDatePicker(endDate) : '',
            carCode: carCode !== 'ALL' ? carCode : '',
            faultCatCode: faultCatCode ? faultCatCode : [''],
        };

        const query = jsonToQueryString(payload);

        (async () => {
            try {
                const res = await restfulManager.getFaultCsv(sessionManager.getAccessToken(), query);
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    fileDownload(json.csv, 'fault.csv');
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await restfulManager.getCars(sessionManager.getAccessToken());
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    const carsArray = json.cars;
                    carsArray.unshift({ carCode: 'ALL' });
                    setCars(carsArray);

                    const defaultCar = carsArray[0];
                    setCarCode(defaultCar.carCode);
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    }, []);

    return (
        <>
            <Box sx={{ flexGrow: 1, padding: '8px' }}>
                <Stack direction="column">
                    <Stack direction="row">
                        <Stack direction="column" sx={{ width: '340px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Date
                            </Typography>
                            <Stack direction="row">
                                <DatePicker
                                    sx={{ ...datePickerStyle, width: '170px'}}
                                    onChange={(newValue) => {
                                        setStartDate(newValue);
                                    }}
                                    defaultValue={startDate}
                                    />
                                <DatePicker
                                    sx={{ ...datePickerStyle, width: '170px'}}
                                    onChange={(newValue) => {
                                        setEndDate(newValue);
                                    }}
                                    defaultValue={endDate}
                                    />
                            </Stack>
                        </Stack>
                        
                        <Stack direction="column" sx={{ marginLeft: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Car No.
                            </Typography>
                            <Select
                                value={carCode}
                                onChange={carSelectChange}
                                sx={Styles.carSelect}
                                MenuProps={{
                                    sx: {
                                        "& .MuiMenuItem-root.Mui-selected": {
                                            color: 'red',
                                            backgroundColor: StyleConstants.mainBgColor,
                                        },
                                        "& .MuiMenuItem-root:hover": {
                                            backgroundColor: StyleConstants.highlightBgColor,
                                        },
                                        "& .MuiMenuItem-root.Mui-selected:hover": {
                                            backgroundColor: StyleConstants.highlightBgColor,
                                        },
                                    }
                                }}
                            >
                                {cars.map((car) => (
                                    <MenuItem
                                        key={car.carCode}
                                        value={car.carCode}
                                        sx={Styles.carSelect}
                                    >
                                        <Typography sx={{ fontSize: Styles.contentFont4 }}>
                                            {car.carCode}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>

                        <Stack direction="column" sx={{ width: '968px', marginLeft: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                System
                            </Typography>
                            <Stack direction="row" sx={{ padding: '7px 16px' }}>
                                <Typography sx={{ color: StyleConstants.fontColor, alignContent: 'center' }}>
                                    Fault Type:
                                </Typography>

                                <FormGroup
                                    row
                                    sx={{
                                        color: StyleConstants.fontColor,
                                    }}>
                                    <FormControlLabel
                                        sx={{ marginLeft: '16px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeAll}
                                                onChange={faultCatCodeAllClick}
                                                name="ALL"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="ALL"
                                    />
                                    <FormControlLabel
                                        sx={{ marginLeft: '8px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeVac}
                                                onChange={faultCatCodeVacClick}
                                                name="VAC"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="VAC"
                                    />
                                    <FormControlLabel
                                        sx={{ marginLeft: '8px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeSiv}
                                                onChange={faultCatCodeSivClick}
                                                name="SIV"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="SIV"
                                    />
                                    <FormControlLabel
                                        sx={{ marginLeft: '8px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeVvvf}
                                                onChange={faultCatCodeVvvfClick}
                                                name="VVVF"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="VVVF"
                                    />
                                    <FormControlLabel
                                        sx={{ marginLeft: '8px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeDoor}
                                                onChange={faultCatCodeDoorClick}
                                                name="DOOR"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="DOOR"
                                    />
                                    <FormControlLabel
                                        sx={{ marginLeft: '8px', }}
                                        control={
                                            <Checkbox
                                                checked={faultCatCodeBrake}
                                                onChange={faultCatCodeBrakeClick}
                                                name="BRAKE"
                                                sx={checkBoxStyle}
                                                />
                                        }
                                        label="BRAKE"
                                    />
                                </FormGroup>
                            </Stack>
                        </Stack>

                        <Button variant="contained" onClick={searchReport} sx={{ marginLeft: '16px' }}>
                            Apply
                        </Button>

                        <Button variant="contained" onClick={exportCsv} sx={{ marginLeft: '16px' }}>
                            Export
                        </Button>
                    </Stack>

                    <Stack direction="row">
                        <Stack direction="column" sx={{ marginTop: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Fault Distribution
                            </Typography>
                            <PieChart
                                series={[
                                    {
                                        data: faultDistributionData,
                                    },
                                ]}
                                width={500}
                                height={280}
                                sx={pieChartStyle}
                            />
                        </Stack>

                        <Stack direction="column" sx={{ marginTop: '16px', marginLeft: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Fault Count Per LRV
                            </Typography>
                            <BarChart
                                dataset={faultCountPerLrvData}
                                yAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'carCode',
                                        label: 'Car No.',
                                    }
                                ]}
                                xAxis={[
                                    {
                                        min: 0,
                                        max: maxFaultCountPerLrv,
                                    }
                                ]}
                                series={[
                                    {
                                        dataKey: 'faultCount',
                                        label: 'No. Of Fault'
                                    }
                                ]}
                                layout="horizontal"
                                grid={{ vertical: true, horizontal: true }}
                                width={654}
                                height={432}
                                sx={barChartStyle}
                            />
                        </Stack>

                        <Stack direction="column" sx={{ marginTop: '16px', marginLeft: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Top 5 Faults
                            </Typography>
                            <BarChart
                                dataset={topFiveFaultData}
                                yAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'faultCode',
                                        label: 'Fault Code',
                                    }
                                ]}
                                xAxis={[
                                    {
                                        min: 0,
                                        max: maxTopFiveFault,
                                    }
                                ]}
                                series={[ { dataKey: 'faultCount', label: 'No. Of Fault' } ]}
                                layout="horizontal"
                                grid={{ vertical: true, horizontal: true }}
                                width={654}
                                height={432}
                                sx={barChartStyle}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction="row">
                        <Stack direction="column" sx={{ marginTop: '16px', backgroundColor: StyleConstants.reportBoxBgColor }}>
                            <Typography sx={{ fontSize: 16, backgroundColor: StyleConstants.reportTitleBgColor, color: StyleConstants.fontColor, textAlign: 'center' }}>
                                Fault Trend
                            </Typography>
                            <BarChart
                                dataset={faultTrendData}
                                yAxis={[
                                    {
                                        min: 0,
                                        max: maxFaultTrend,
                                    }
                                ]}
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'yearMonth',
                                    }
                                ]}
                                series={faultTrendSeries}
                                width={800}
                                height={400}
                                sx={{
                                    ...barChartStyle,
                                    [`.${axisClasses.left} .${axisClasses.label}`]: {
                                    transform: 'translate(-20px, 0)',
                                    },
                                }}
                            />
                        </Stack>

                        <FaultTable isLoading={isLoading} faults={faults} rowsPerPage={10} />
                    </Stack>
                </Stack>
            </Box>
        </>
    );
};

export default ReportPage;